import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, effect, inject, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { RouterLink } from '@angular/router';

import { Store } from '@ngrx/store';

import { FiltersAndPaginationPayloadService } from '../../../@core/services/filters-and-pagination-payload.service';
import { QuotesActions } from '../../../@store/quotes/quotes.actions';
import {
  selectActiveQuotes,
  selectActiveQuotesError,
  selectActiveQuotesLoading,
  selectActiveQuotesTotalCount,
} from '../../../@store/quotes/quotes.selectors';
import { Quote, QuoteTableData } from '../../../@store/quotes/quotes.state.interface';
import { CONFIG } from '../../../config';
import { SafeAny } from '../../../types/safe-any';
import { PaginationComponent } from '../../@shared/pagination/pagination.component';

@Component({
  selector: 'ciwc-my-quotes',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatProgressSpinnerModule,
    PaginationComponent,
    MatIconModule,
    MatButtonModule,
    RouterLink,
  ],
  templateUrl: './my-quotes.component.html',
  styleUrls: ['./my-quotes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyQuotesComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly filtersAndPaginationPayloadService = inject(FiltersAndPaginationPayloadService);
  readonly activeQuotes = this.store.selectSignal(selectActiveQuotes);
  readonly activeQuotesLength = this.store.selectSignal(selectActiveQuotesTotalCount);
  readonly quotesLoading = this.store.selectSignal(selectActiveQuotesLoading);
  readonly quotesError = this.store.selectSignal(selectActiveQuotesError);
  readonly CONFIG = CONFIG;

  private myQuotesFilters: SafeAny;

  pageSizeOptions = CONFIG.PAGE_SIZE_OPTIONS;
  pageSize = CONFIG.PAGE_SIZE;
  totalMyQuotes = 0;
  pageIndex = 0;
  displayedColumns: string[] = [
    'quoteId',
    'description',
    'caNumber',
    'quantity',
    'dateCreated',
    'status',
    'client',
    'assignee',
    'dateUpdated',
    'actions',
  ];
  currentSort: Sort = { active: 'dateCreated', direction: 'desc' };
  dataSource: MatTableDataSource<QuoteTableData> = new MatTableDataSource<QuoteTableData>();
  errorText = CONFIG.ERROR_MESSAGES.DEFAULT;

  constructor() {
    effect(() => {
      this.initValues();
    });
  }

  ngOnInit(): void {
    this.fetchData();
    this.initFilterValues();
  }

  sortData(sort: Sort): void {
    this.currentSort = sort;
    const formattedSort = sort.direction === 'desc' ? `-${sort.active}` : sort.active;

    this.filtersAndPaginationPayloadService.updateSortFromMaterial(this.currentSort);

    this.store.dispatch(
      QuotesActions.loadActiveQuotes({
        payload: {
          offset: this.pageIndex + 1,
          limit: this.pageSize,
          sort: formattedSort,
          assigneeId: localStorage.getItem('id'),
          clientId: this.myQuotesFilters?.client,
          status: this.myQuotesFilters?.status || null,
          archived: false,
        },
      }),
    );
  }

  handlePageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageIndex = event.pageIndex;

    const formattedSort =
      this.currentSort.direction === 'desc'
        ? `-${this.currentSort.active}`
        : this.currentSort.active;

    this.store.dispatch(
      QuotesActions.loadActiveQuotes({
        payload: {
          offset: this.pageIndex + 1,
          limit: this.pageSize,
          sort: formattedSort,
          assigneeId: localStorage.getItem('id'),
          clientId: this.myQuotesFilters?.client,
          status: this.myQuotesFilters?.status || null,
          archived: false,
        },
      }),
    );
  }

  private initValues(): void {
    const activeQuotesTableData = this.activeQuotes()?.map((quote: Quote) => ({
      quoteId: quote.quoteId,
      client: `${quote.client.firstName} ${quote.client.lastName}, ${quote.client.company}, ${quote.client.code}`,
      dateCreated: quote.dateCreated,
      status: quote.status,
      caNumber: quote.caNumber?.number || '',
      description: quote.description || '',
      quantity: quote.quantityBreaks ? quote.quantityBreaks[0]?.quantity || '' : '',
      assignee: quote.assignee ? `${quote.assignee.firstName} ${quote.assignee.lastName}` : 'N/A',
      lastEdited: quote.dateUpdated || quote.dateCreated,
      id: quote.id,
    }));
    this.dataSource.data = activeQuotesTableData as QuoteTableData[];
    this.totalMyQuotes = this.activeQuotesLength();
  }

  private fetchData(): void {
    this.store.dispatch(QuotesActions.loadStatuses({ payload: { identifier: 'QUOTES_STATUS' } }));
    this.store.dispatch(
      QuotesActions.loadActiveQuotes({
        payload: {
          offset: 1,
          limit: 10,
          sort: '-dateCreated',
          assigneeId: localStorage.getItem('id'),
          archived: false,
        },
      }),
    );

    this.filtersAndPaginationPayloadService.updateSortFromMaterial(this.currentSort);
  }

  private initFilterValues(): void {
    this.filtersAndPaginationPayloadService.myQuotesFilterPayload$.subscribe((filter) => {
      this.myQuotesFilters = filter;
      this.pageIndex = 0;
      this.pageSize = 10;
    });
  }
}
